import { addDays, getDate, getHours, getMinutes, getMonth, getYear, parseISO } from 'date-fns';
import { AgeType } from '@eti/schema-types';

export const getNumberOfAdults = (travelers) =>
  travelers.filter((traveler) => traveler.ageType === AgeType.Adt).length;

export const getDateTimeParts = (tripDate) => {
  const parsedDate = parseISO(tripDate);
  const year = getYear(parsedDate);
  const month = getMonth(parsedDate) + 1;
  const day = getDate(parsedDate);
  const hours = getHours(parsedDate);
  const minutes = getMinutes(parsedDate);

  return { year, month, day, hours, minutes };
};

const buildUrl = (urlBase, params) => {
  const url = new URL(urlBase);
  url.search = new URLSearchParams(params).toString();
  return url.toString();
};

export const createBookingHotelsDeeplink = (
  arrivalDate,
  returnDate,
  cityCode,
  numberOfAdults,
  numberOfChildren,
  language,
  siteCurrency,
  label,
  affiliateId,
) => {
  const AID = 2164554;
  const checkoutDate = returnDate ?? addDays(new Date(arrivalDate), 7).toISOString();

  const { year: checkinYear, month: checkinMonth, day: checkinDay } = getDateTimeParts(arrivalDate);
  const {
    year: checkoutYear,
    month: checkoutMonth,
    day: checkoutDay,
  } = getDateTimeParts(checkoutDate);

  const params = {
    aid: affiliateId ?? AID,
    checkin_year: checkinYear,
    checkin_month: checkinMonth,
    checkin_monthday: checkinDay,
    checkout_year: checkoutYear,
    checkout_month: checkoutMonth,
    checkout_monthday: checkoutDay,
    label,
    iata_orr: 1,
    iata: cityCode,
    lang: language,
    selected_currency: siteCurrency,
    group_adults: numberOfAdults,
    group_children: numberOfChildren,
  };

  return buildUrl('https://booking.com/searchresults.html', params);
};

const getSearchInfo = (bounds) => {
  const firstBound = bounds[0];
  const allFirstBoundSegments = firstBound?.segments ?? [];
  const lastFirstBoundSegment = allFirstBoundSegments[allFirstBoundSegments.length - 1];
  const arrivalDate = lastFirstBoundSegment?.arrivedAt;
  const returnDate = bounds[1]?.segments[0]?.departuredAt;
  const destination = lastFirstBoundSegment?.destination;
  return { arrivalDate, returnDate, destination };
};

export const resolveDeepLinkToBooking = (
  bounds = [],
  language,
  siteCurrency,
  travelers = [],
  label,
  affiliateId,
) => {
  const { arrivalDate, returnDate, destination } = getSearchInfo(bounds);
  const numberOfAdults = getNumberOfAdults(travelers);
  const numberOfChildren = travelers?.length - numberOfAdults;

  return createBookingHotelsDeeplink(
    arrivalDate,
    returnDate,
    destination.cityCode,
    numberOfAdults,
    numberOfChildren,
    language,
    siteCurrency,
    label,
    affiliateId,
  );
};

export const createBookingCarsDeeplink = (
  arrivalDate,
  departureDate,
  destinationName,
  destinationCityCode,
  affiliateId,
  label,
) => {
  const dropOffDate = departureDate ?? addDays(new Date(arrivalDate), 7).toISOString();

  const {
    year: puYear,
    month: puMonth,
    day: puDay,
    hours: puHour,
    minutes: puMinute,
  } = getDateTimeParts(arrivalDate);

  const {
    year: doYear,
    month: doMonth,
    day: doDay,
    hours: doHour,
    minutes: doMinute,
  } = getDateTimeParts(dropOffDate);

  let updatedPuHour = puHour;
  let updatedPuMinute = puMinute;
  let updatedDoHour = doHour;
  let updatedDoMinute = doMinute;

  if (puMinute > 0) {
    updatedPuHour = puHour + 1;
    updatedPuMinute = 0;
  }

  if (doMinute > 0) {
    updatedDoHour = doHour - 1;
    updatedDoMinute = 0;
  }

  const params = {
    aid: affiliateId,
    label,
    locationIata: destinationCityCode,
    dropLocationIata: destinationCityCode,
    locationName: destinationName,
    dropLocationName: destinationName,
    driversAge: 30,
    puDay,
    puMonth,
    puYear,
    puHour: updatedPuHour,
    puMinute: updatedPuMinute,
    doDay,
    doMonth,
    doYear,
    doHour: updatedDoHour,
    doMinute: updatedDoMinute,
  };

  return buildUrl('https://cars.booking.com/search-results', params);
};

export const resolveDeeplinkForCarBanner = (bounds, label, affiliateId) => {
  const { arrivalDate, returnDate, destination } = getSearchInfo(bounds);

  return createBookingCarsDeeplink(
    arrivalDate,
    returnDate,
    destination.cityName,
    destination.cityCode,
    affiliateId,
    label,
  );
};
